import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toaster, type Toast, type ToastAction } from '@forecastify/ui';
import { useT } from '../../i18n';
import { ScreenState } from '../screen-state';
import type { DealTabProps } from './deal-tab';
import { WinnerDialog } from './scenario-dialogs';
import { ScenarioEditor } from './scenario-editor';
import { useScenarioCall, useScenarioList } from './scenario-kit';
import { ScenarioList } from './scenario-list';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · the record's *Scenario* tab. DM-4, DS-1…6.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The list, or one scenario open in the editor — which one is `?scenario=<uuid>` in the address,
 * so a reload or a shared link lands where it was and the browser's back returns to the list.
 *
 * ⚠️⚠️ **MARKING A WINNER REPLACES THE PLAN** (DS-1). The answer is the deal, so the record is
 * reloaded — Terv, Erőforrás and the header's value move — and the toast offers the way to the
 * plan rather than switching tabs under somebody who may want to keep comparing.
 */

export function DealScenarioTab({ deal, record, feeds, canManage, onTab }: DealTabProps) {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get('scenario');
  const list = useScenarioList(deal.id);
  const call = useScenarioCall(deal.id);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const [winner, setWinner] = useState<{ id: string; name: string } | null>(null);
  const [revision, setRevision] = useState(0);

  /* DS-6 · `deal.manage`, and a won or lost deal's scenarios are read-only. */
  const writable = canManage && deal.statusCategory === 'open';
  const rateCurrency = feeds.config?.settings.currencyCode ?? deal.currencyCode;

  const toast = useCallback(
    (title: string, variant: Toast['variant'] = 'success', action?: ToastAction) => {
      setToasts((current) => [
        ...current,
        {
          id: `${String(Date.now())}-${title}`,
          title,
          variant,
          ...(action === undefined ? {} : { action }),
        },
      ]);
    },
    [],
  );

  const open = useCallback(
    (id: string | null) => {
      setSearchParams((current) => {
        const params = new URLSearchParams(current);
        if (id === null) params.delete('scenario');
        else params.set('scenario', id);
        return params;
      });
    },
    [setSearchParams],
  );

  const markWinner = async (): Promise<string | null> => {
    if (winner === null) return null;
    const answer = await call(`/${winner.id}/winner`, 'POST');
    if (typeof answer === 'string') return answer;
    const name = winner.name;
    setWinner(null);
    await Promise.all([record.reload(), list.reload()]);
    setRevision((count) => count + 1);
    toast(t('dealScenario.toast.won', { name }), 'success', {
      label: t('dealScenario.action.openPlan'),
      /* Switching tabs cannot fail; the type asks for the sentence anyway. */
      failureLabel: t('error.unavailable'),
      onAction: () => {
        onTab('plan');
      },
    });
    return null;
  };

  return (
    <div className="flex flex-col">
      <ScreenState failure={list.failure} onRetry={() => void list.reload()}>
        {selected === null ? (
          <ScenarioList
            deal={deal}
            scenarios={list.scenarios}
            loading={list.loading}
            writable={writable}
            call={call}
            onOpen={open}
            onMarkWinner={setWinner}
            onChanged={() => void list.reload()}
            onDealChanged={() => void record.reload()}
            toast={toast}
          />
        ) : (
          <ScenarioEditor
            key={selected}
            deal={deal}
            scenarioId={selected}
            writable={writable}
            revision={revision}
            rateCurrency={rateCurrency}
            profiles={feeds.config?.profiles ?? []}
            onBack={() => {
              open(null);
              void list.reload();
            }}
            onOpen={open}
            onMarkWinner={setWinner}
            onTab={onTab}
            onDealChanged={() => void record.reload()}
            toast={toast}
          />
        )}
      </ScreenState>
      <WinnerDialog
        name={winner?.name ?? null}
        onCancel={() => {
          setWinner(null);
        }}
        onConfirm={markWinner}
      />
      <Toaster
        toasts={[...toasts]}
        label={t('deal.tab.scenario')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
      />
    </div>
  );
}
